import React, { createContext, useContext, useState, useReducer } from 'react';

interface GameRating {
  userId: string;
  rating: number;
  timestamp: number;
}

interface LocalGame {
  id: number;
  title: string;
  description: string;
  image: string;
  owner: string;
  available: boolean;
  category: string;
  minPlayers: number;
  maxPlayers: number;
  playTime: string;
  age: string;
  rating: number;
  ratingCount: number;
  ratings: GameRating[];
  state: string;
  city: string;
  zipCode: string;
}

interface GameState {
  games: LocalGame[];
  favorites: Set<number>;
  currentUser: {
    id: number;
    name: string;
    email: string;
  };
}

interface GameActionAddGame {
  type: 'ADD_GAME';
  payload: Record<string, unknown>;
}
interface GameActionUpdateGame {
  type: 'UPDATE_GAME';
  payload: Record<string, unknown> & { id: number };
}
interface GameActionAddRating {
  type: 'ADD_RATING';
  payload: { gameId: number; userId: string; rating: number };
}
interface GameActionToggleFavorite {
  type: 'TOGGLE_FAVORITE';
  payload: { gameId: number };
}
interface GameActionRemoveFavorite {
  type: 'REMOVE_FAVORITE';
  payload: { gameId: number };
}

type GameAction =
  | GameActionAddGame
  | GameActionUpdateGame
  | GameActionAddRating
  | GameActionToggleFavorite
  | GameActionRemoveFavorite;

const GameContext = createContext<any>(undefined);

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

const initialState = {
  games: [
    {
      id: 1,
      title: 'Catan',
      description:
        'Trade, build, and settle the island of Catan in this classic strategy game.',
      image: './assets/ezgif-7fb6d2213a2797b0.jpg',
      owner: 'John Doe',
      available: true,
      category: 'Strategy',
      minPlayers: 3,
      maxPlayers: 4,
      playTime: '60-90 minutes',
      age: '10+',
      rating: 0,
      ratingCount: 0,
      ratings: [] as any[],
      state: 'California',
      city: 'Los Angeles',
      zipCode: '90210',
    },
    {
      id: 2,
      title: 'Ticket to Ride',
      description:
        'Build railway routes across North America in this award-winning game.',
      image: '/assets/ezgif-7f27845d969e8812.jpg',
      owner: 'Jane Smith',
      available: true,
      category: 'Family',
      minPlayers: 2,
      maxPlayers: 5,
      playTime: '30-60 minutes',
      age: '8+',
      rating: 0,
      ratingCount: 0,
      ratings: [] as any[],
      state: 'New York',
      city: 'New York City',
      zipCode: '10001',
    },
    {
      id: 3,
      title: 'Pandemic',
      description:
        'Work together to save the world from deadly diseases in this cooperative game.',
      image: './assets/ezgif-71b24d75cf54ba3a.jpg',
      owner: 'Mike Johnson',
      available: false,
      category: 'Cooperative',
      minPlayers: 2,
      maxPlayers: 4,
      playTime: '45-60 minutes',
      age: '8+',
      rating: 0,
      ratingCount: 0,
      ratings: [] as any[],
      state: 'Texas',
      city: 'Houston',
      zipCode: '77001',
    },
    {
      id: 4,
      title: 'Wingspan',
      description:
        'Attract birds to your wildlife preserves in this engine-building game.',
      image: './assets/ezgif-75a64833179cafc4.jpg',
      owner: 'Sarah Wilson',
      available: true,
      category: 'Strategy',
      minPlayers: 1,
      maxPlayers: 5,
      playTime: '40-70 minutes',
      age: '10+',
      rating: 0,
      ratingCount: 0,
      ratings: [] as any[],
      state: 'Florida',
      city: 'Miami',
      zipCode: '33101',
    },
  ],
  // borrowRequests removed - now handled by Convex queries
  favorites: new Set<number>(),
  currentUser: {
    id: 1,
    name: 'Current User',
    email: 'user@example.com',
  },
};

const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'ADD_GAME':
      return {
        ...state,
        games: [
          ...state.games,
          { ...action.payload, id: Date.now() } as LocalGame,
        ],
      };
    case 'UPDATE_GAME':
      return {
        ...state,
        games: state.games.map((game: LocalGame) =>
          game.id === action.payload.id
            ? ({ ...game, ...action.payload } as LocalGame)
            : game
        ),
      };
    case 'ADD_RATING':
      return {
        ...state,
        games: state.games.map((game: LocalGame) => {
          if (game.id === action.payload.gameId) {
            const existingRatingIndex = game.ratings.findIndex(
              (r: GameRating) => r.userId === action.payload.userId
            );
            let newRatings = [...game.ratings];

            if (existingRatingIndex >= 0) {
              // Update existing rating
              newRatings[existingRatingIndex] = {
                userId: action.payload.userId,
                rating: action.payload.rating,
                timestamp: Date.now(),
              };
            } else {
              // Add new rating
              newRatings.push({
                userId: action.payload.userId,
                rating: action.payload.rating,
                timestamp: Date.now(),
              });
            }

            // Calculate new average rating
            const totalRating = newRatings.reduce(
              (sum, r) => sum + r.rating,
              0
            );
            const averageRating = totalRating / newRatings.length;

            return {
              ...game,
              ratings: newRatings,
              ratingCount: newRatings.length,
              rating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
            };
          }
          return game;
        }),
      };
    // Removed ADD_BORROW_REQUEST and UPDATE_BORROW_REQUEST cases
    // Borrow requests are now managed via Convex mutations/queries
    case 'TOGGLE_FAVORITE':
      return {
        ...state,
        favorites: new Set(
          state.favorites.has(action.payload.gameId)
            ? [...state.favorites].filter((id) => id !== action.payload.gameId)
            : [...state.favorites, action.payload.gameId]
        ),
      };
    case 'REMOVE_FAVORITE':
      return {
        ...state,
        favorites: new Set(
          [...state.favorites].filter((id) => id !== action.payload.gameId)
        ),
      };
    default:
      return state;
  }
};

export const GameProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [filterCity, setFilterCity] = useState('');
  const [filterZipCode, setFilterZipCode] = useState('');

  const addGame = (gameData: Record<string, unknown>) => {
    dispatch({ type: 'ADD_GAME', payload: gameData });
  };

  const updateGame = (gameId: number, updates: Record<string, unknown>) => {
    dispatch({ type: 'UPDATE_GAME', payload: { id: gameId, ...updates } });
  };

  // Removed addBorrowRequest and updateBorrowRequest functions
  // Use Convex mutations instead:
  // - createBorrowRequest (from src/convex/borrow.js)
  // - updateBorrowRequestStatus (from src/convex/borrow.js)

  const addRating = (gameId: number, rating: number) => {
    dispatch({
      type: 'ADD_RATING',
      payload: {
        gameId,
        userId: state.currentUser.id.toString(),
        rating,
      },
    });
  };

  const toggleFavorite = (gameId: number) => {
    dispatch({ type: 'TOGGLE_FAVORITE', payload: { gameId } });
  };

  const removeFavorite = (gameId: number) => {
    dispatch({ type: 'REMOVE_FAVORITE', payload: { gameId } });
  };

  const filteredGames = state.games.filter((game: LocalGame) => {
    const matchesSearch =
      game.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filterCategory === 'all' || game.category === filterCategory;
    const matchesState = filterState === 'all' || game.state === filterState;
    const matchesCity =
      !filterCity || game.city.toLowerCase().includes(filterCity.toLowerCase());
    const matchesZipCode =
      !filterZipCode || game.zipCode.includes(filterZipCode);
    return (
      matchesSearch &&
      matchesCategory &&
      matchesState &&
      matchesCity &&
      matchesZipCode
    );
  });

  const value = {
    ...state,
    searchTerm,
    setSearchTerm,
    filterCategory,
    setFilterCategory,
    filterState,
    setFilterState,
    filterCity,
    setFilterCity,
    filterZipCode,
    setFilterZipCode,
    filteredGames,
    addGame,
    updateGame,
    // Removed addBorrowRequest and updateBorrowRequest from context
    // Components should use Convex hooks directly (useMutation, useQuery)
    addRating,
    toggleFavorite,
    removeFavorite,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
